import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { canAccessPath } from "@/lib/rbac";
import { LanguageProvider } from "@/context/LanguageContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import ReferralTracker from "@/components/ReferralTracker";
import Login from "@/pages/Login";
import Foundation from "@/pages/Foundation";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Dashboard from "@/pages/Dashboard";
import Patients from "@/pages/Patients";
import Demandes from "@/pages/Demandes";
import DemandeDetail from "@/pages/DemandeDetail";
import Parties from "@/pages/Parties";
import Admin from "@/pages/Admin";
import Conformite from "@/pages/Conformite";
import WSI from "@/pages/WSI";
import WSIViewer from "@/pages/WSIViewer";
import Analyses from "@/pages/Analyses";
import AnalyseDetail from "@/pages/AnalyseDetail";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import Paillasse from "@/pages/Paillasse";
import Intervenants from "@/pages/Intervenants";
import VideoShare from "@/pages/VideoShare";
import ResetPassword from "@/pages/ResetPassword";
import RelierPrescripteurs from "@/pages/RelierPrescripteurs";
import MesRapports from "@/pages/MesRapports";

function AccessDenied() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="access-denied">
      <ShieldAlert className="h-12 w-12 text-red-500 mb-4" />
      <h1 className="font-head text-2xl font-bold text-slate-900">Accès refusé</h1>
      <p className="text-sm text-slate-500 mt-2 max-w-md">
        Votre rôle ne dispose pas des autorisations nécessaires pour accéder à cette section
        (séparation stricte des accès — Loi 25).
      </p>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null)
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const allowed = canAccessPath(user.role, location.pathname);
  return <Layout>{allowed ? children : <AccessDenied />}</Layout>;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Chargement…</div>;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

export default function App() {
  return (
    <ThemeProvider>
    <LanguageProvider>
    <AuthProvider>
      <ReferralTracker />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/fondation" element={<Foundation />} />
          <Route path="/v" element={<VideoShare />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/patients" element={<Protected><Patients /></Protected>} />
          <Route path="/demandes" element={<Protected><Demandes /></Protected>} />
          <Route path="/demandes/relier-prescripteurs" element={<Protected><RelierPrescripteurs /></Protected>} />
          <Route path="/demandes/:id" element={<Protected><DemandeDetail /></Protected>} />
          <Route path="/parties" element={<Protected><Parties /></Protected>} />
          <Route path="/wsi" element={<Protected><WSI /></Protected>} />
          <Route path="/wsi/:id" element={<Protected><WSIViewer /></Protected>} />
          <Route path="/analyses" element={<Protected><Analyses /></Protected>} />
          <Route path="/analyses/:id" element={<Protected><AnalyseDetail /></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/mes-rapports" element={<Protected><MesRapports /></Protected>} />
          <Route path="/paillasse" element={<Protected><Paillasse /></Protected>} />
          <Route path="/intervenants" element={<Protected><Intervenants /></Protected>} />
          <Route path="/admin" element={<Protected><Admin /></Protected>} />
          <Route path="/conformite" element={<Protected><Conformite /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </AuthProvider>
    </LanguageProvider>
    </ThemeProvider>
  );
}
